import { Job, SupabaseJob } from '@applicator/shared'

import SearchInput from '../../SearchInput'
import JobListItem from '../JobListItem'

interface JobsListColumnProps {
  leftColumnRef: React.RefObject<HTMLDivElement>
  isInitialMount: boolean
  isReturning: boolean
  jobSearchQuery: string
  setJobSearchQuery: (value: string) => void
  setDisplayedJobsCount: (value: number | ((prev: number) => number)) => void
  setAllJobsLoaded: (value: boolean) => void
  relevanceFilter: 'all' | 'relevant' | 'not-relevant'
  setRelevanceFilter: (value: 'all' | 'relevant' | 'not-relevant') => void
  jobsListScrollRef: React.RefObject<HTMLDivElement>
  loading: boolean
  error: string | null
  filteredJobs: Job[]
  displayedJobsCount: number
  allJobsLoaded: boolean
  isLoadingMore: boolean
  loadMoreTriggerRef: React.RefObject<HTMLDivElement>
  supabaseJobs: SupabaseJob[]
  selectedJobId: string | null
  handleJobSelect: (jobId: string) => void
  jobItemRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
}

export function JobsListColumn({
  leftColumnRef,
  isInitialMount,
  isReturning,
  jobSearchQuery,
  setJobSearchQuery,
  setDisplayedJobsCount,
  setAllJobsLoaded,
  relevanceFilter,
  setRelevanceFilter,
  jobsListScrollRef,
  loading,
  error,
  filteredJobs,
  displayedJobsCount,
  allJobsLoaded,
  isLoadingMore,
  loadMoreTriggerRef,
  supabaseJobs,
  selectedJobId,
  handleJobSelect,
  jobItemRefs,
}: JobsListColumnProps) {
  return (
    <div
      ref={leftColumnRef}
      className="border-r border-border-default bg-surface-default transition-all duration-500 ease-in-out flex flex-col"
      style={{
        width: isInitialMount ? '100%' : isReturning ? '100%' : '25%',
        opacity: 1,
        willChange: isInitialMount || isReturning ? 'width' : 'auto',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Sticky Search Bar and Filter */}
      <div className="sticky top-0 z-10 bg-surface-default border-b border-border-default p-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <SearchInput
              value={jobSearchQuery}
              onChange={(value) => {
                setJobSearchQuery(value)
                setDisplayedJobsCount(20) // Reset lazy loading when search changes
                setAllJobsLoaded(false)
              }}
              placeholder="Search jobs or companies..."
              showClearButton
            />
          </div>
          {/* Relevance Filter */}
          <select
            value={relevanceFilter}
            onChange={(e) => {
              setRelevanceFilter(e.target.value as RelevanceFilter)
              setDisplayedJobsCount(20) // Reset lazy loading when filter changes
              setAllJobsLoaded(false)
            }}
            className="pl-4 pr-10 py-2 border border-border-strong rounded-md focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:border-transparent appearance-none bg-surface-default flex-shrink-0"
            style={{
              width: 'auto',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23374151'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '1.5em 1.5em',
            }}
          >
            <option value="all">All</option>
            <option value="relevant">Relevant</option>
            <option value="not-relevant">Not relevant</option>
          </select>
        </div>
      </div>
      {/* Scrollable Jobs List */}
      <div 
        ref={jobsListScrollRef}
        className="flex-1 job-list-scrollable" 
        style={{ 
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="p-6 pt-4">
          <div className="space-y-1">
            {loading ? (
              <div className="text-text-secondary text-center py-4">Loading jobs...</div>
            ) : error ? (
              <div className="text-danger text-center py-4">Error: {error}</div>
            ) : (
              <>
                {filteredJobs.length === 0 ? (
                  <div className="text-text-muted text-center py-4">
                    No jobs found matching "{jobSearchQuery}"
                  </div>
                ) : (
                  <>
                    {/* Отображаем только первые displayedJobsCount элементов из отфильтрованного списка */}
                    {filteredJobs.slice(0, displayedJobsCount).map((job) => {
                      const supabaseJob = supabaseJobs.find(j => j.id === job.id)
                      // Callback ref to store element in map
                      const setItemRef = (el: HTMLDivElement | null) => {
                        if (job.id) {
                          if (el) {
                            jobItemRefs.current.set(job.id, el)
                          } else {
                            jobItemRefs.current.delete(job.id)
                          }
                        }
                      }
                      return (
                        <JobListItem
                          key={job.id || job.position}
                          job={job}
                          index={0}
                          isSelected={selectedJobId === job.id}
                          onSelect={() => job.id && handleJobSelect(job.id)}
                          supabaseJob={supabaseJob}
                          itemRef={setItemRef}
                        />
                      )
                    })}
                    {/* Trigger элемент для lazy loading (видимый при 70% прокрутки) */}
                    {!allJobsLoaded && filteredJobs.length > displayedJobsCount && (
                      <div
                        ref={loadMoreTriggerRef}
                        className="h-20 flex items-center justify-center"
                      >
                        {isLoadingMore && (
                          <div className="text-text-muted text-sm">Loading more...</div>
                        )}
                      </div>
                    )}
                    {/* Индикатор, что все загружено */}
                    {allJobsLoaded && filteredJobs.length > 20 && (
                      <div className="text-center text-text-muted text-sm py-4">
                        All jobs loaded ({filteredJobs.length} total)
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
